/**
 * Forms theming model — the single source of truth that maps a form's
 * persisted `appearance` blob to a fully-resolved `FormTheme` of tokens, and
 * those tokens to CSS variables. The web + native providers and the editor
 * previews all consume this, so tint (and future tokens) change in ONE place.
 */
import type {
  FormAppearance,
  FormButtonCorners,
  FormButtonFill,
  FormHeaderBackground,
} from "./types";

/** Curated accent swatches offered in the Appearance picker. */
export const ACCENT_SWATCHES = [
  "#2f6fdb",
  "#1f8a5b",
  "#6d4bf0",
  "#d9773a",
  "#c0303c",
  "#0e7d86",
  "#1f2a3d",
] as const;

/**
 * Default accent used when a form has no per-form appearance. The forms
 * design-language blue (see the Elements handover).
 */
export const DEFAULT_ACCENT = "#2f6fdb";

/**
 * Static (appearance-independent) design tokens — the SINGLE source of truth
 * for the forms UI's neutrals, semantic colours and input geometry. Change a
 * value here and it updates every element (they read the emitted CSS vars,
 * see `formStaticCssVars`). Kept beside the theme so tokens live in one file.
 */
export const FORMS_TOKENS = {
  /** Primary ink / label + body text. */
  ink: "#1f2a3d",
  /** Muted text (sub-labels). */
  muted: "#6a7383",
  /** Softer muted (meta, help text). */
  mutedSoft: "#8a93a3",
  /** Faint text (placeholders, empty values). */
  faint: "#aab2bd",
  /** OPTIONAL tag colour. */
  optional: "#9aa3b0",
  /** Input / card border at rest. */
  border: "#dfe3e9",
  /** Lighter divider border. */
  borderLight: "#e6e9ee",
  /** Page background behind cards. */
  page: "#eef1f4",
  /** Field rail — required & still to complete (a "to-do", not an error). */
  railAmber: "#e8991a",
  /** Field rail + input border — validation error (post-submit). */
  railRed: "#c0303c",
  /** Success / completed-required check + good score. */
  success: "#1f8a5b",
  /** Toggle switch OFF track. */
  toggleOff: "#d4dae1",
  /** Input corner radius. */
  inputRadius: "10px",
  /** Input border width (at rest; focus goes to 1.5px accent). */
  inputBorderWidth: "1px",
  /** Filled-input background — a field with a value (from the library). */
  inputFilledBg: "#f4f8ff",
  /** Filled-input border. */
  inputFilledBorder: "#cdd9ec",
  /** Input addon background (URL/phone prefix, number stepper divider). */
  addonBg: "#f7f9fb",
  /** Field sizing — overridable per density (see formCompactCssVars). */
  inputPaddingX: "13px",
  inputPaddingY: "11px",
  inputFontSize: "14px",
  labelFontSize: "14px",
  tabFontSize: "13px",
} as const;

const CORNER_RADIUS_PX: Record<FormButtonCorners, number> = {
  square: 6,
  rounded: 10,
  pill: 999,
};

/** A fully-resolved theme — every token populated, no optionals. */
export interface FormTheme {
  accent: string;
  /** Readable text colour on top of `accent` (#fff or #1f2a3d). */
  accentContrast: string;
  button: { radius: number; fill: FormButtonFill };
  header: { showLogo: boolean; background: FormHeaderBackground };
}

export const DEFAULT_FORM_THEME: FormTheme = {
  accent: DEFAULT_ACCENT,
  accentContrast: "#ffffff",
  button: { radius: CORNER_RADIUS_PX.rounded, fill: "solid" },
  header: { showLogo: true, background: "tint" },
};

/** Parse a #rrggbb hex into [r,g,b], or null if malformed. */
function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const int = parseInt(m[1], 16);
  return [(int >> 16) & 0xff, (int >> 8) & 0xff, int & 0xff];
}

/** Pick black/white text for readable contrast on a hex background. */
export function readableTextColor(hex: string): string {
  const rgb = parseHex(hex);
  if (!rgb) return "#ffffff";
  const [r, g, b] = rgb;
  // Relative luminance (sRGB, perceptual weights).
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#1f2a3d" : "#ffffff";
}

const rgba = (hex: string, a: number): string => {
  const rgb = parseHex(hex) || [43, 122, 234];
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})`;
};

/** Darken a hex by a factor (0–1), for button hover states. */
const darken = (hex: string, factor: number): string => {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const d = rgb.map((c) => Math.max(0, Math.round(c * (1 - factor))));
  return `#${d.map((c) => c.toString(16).padStart(2, "0")).join("")}`;
};

/**
 * Resolve the persisted appearance (or null) into a full FormTheme, filling
 * every missing token with a platform default. The one place appearance →
 * theme logic lives.
 */
export function resolveFormTheme(
  appearance?: FormAppearance | null,
): FormTheme {
  const accent = appearance?.accent || DEFAULT_ACCENT;
  const corners: FormButtonCorners = appearance?.button?.corners || "rounded";
  return {
    accent,
    accentContrast: readableTextColor(accent),
    button: {
      radius: CORNER_RADIUS_PX[corners],
      fill: appearance?.button?.fill || "solid",
    },
    header: {
      showLogo: appearance?.header?.showLogo !== false,
      background: appearance?.header?.background || "tint",
    },
  };
}

/**
 * Static design tokens as CSS custom properties. Appearance-independent, so
 * they can be injected high in the render tree (e.g. DocumentView) — every
 * surface resolves `var(--forms-*)` without needing a per-form theme wrapper.
 * These never carry the per-form accent, so injecting them nested never
 * clobbers a FormThemeProvider's accent.
 */
export function formStaticCssVars(): Record<string, string> {
  return {
    "--forms-ink": FORMS_TOKENS.ink,
    "--forms-muted": FORMS_TOKENS.muted,
    "--forms-muted-soft": FORMS_TOKENS.mutedSoft,
    "--forms-faint": FORMS_TOKENS.faint,
    "--forms-optional": FORMS_TOKENS.optional,
    "--forms-border": FORMS_TOKENS.border,
    "--forms-border-light": FORMS_TOKENS.borderLight,
    "--forms-page": FORMS_TOKENS.page,
    "--forms-rail-amber": FORMS_TOKENS.railAmber,
    "--forms-rail-red": FORMS_TOKENS.railRed,
    "--forms-success": FORMS_TOKENS.success,
    "--forms-toggle-off": FORMS_TOKENS.toggleOff,
    "--forms-input-radius": FORMS_TOKENS.inputRadius,
    "--forms-input-border-w": FORMS_TOKENS.inputBorderWidth,
    "--forms-input-filled-bg": FORMS_TOKENS.inputFilledBg,
    "--forms-input-filled-border": FORMS_TOKENS.inputFilledBorder,
    "--forms-addon-bg": FORMS_TOKENS.addonBg,
    // Field sizing (default density). Compact overrides these — see
    // formCompactCssVars — so the same elements render smaller in dense
    // hosts like the editor's element-config sidebar.
    "--forms-input-px": FORMS_TOKENS.inputPaddingX,
    "--forms-input-py": FORMS_TOKENS.inputPaddingY,
    "--forms-input-font": FORMS_TOKENS.inputFontSize,
    "--forms-label-font": FORMS_TOKENS.labelFontSize,
    "--forms-tab-font": FORMS_TOKENS.tabFontSize,
    // Accent fallbacks for surfaces rendered without a FormThemeProvider
    // (the editor Preview tab / design canvas). Distinct names so they never
    // override a real `--forms-accent`.
    "--forms-accent-default": DEFAULT_ACCENT,
    "--forms-accent-soft-default": rgba(DEFAULT_ACCENT, 0.06),
    "--forms-accent-50-default": rgba(DEFAULT_ACCENT, 0.5),
    "--forms-accent-hover-default": darken(DEFAULT_ACCENT, 0.12),
    "--forms-accent-ring-default": rgba(DEFAULT_ACCENT, 0.14),
  };
}

/**
 * Compact field-sizing overrides — apply on a subtree (e.g. the editor's
 * element-config sidebar) so the same form elements render smaller without
 * changing how they look on the actual form. Only the size vars; colours,
 * borders and radius are unchanged.
 */
export function formCompactCssVars(): Record<string, string> {
  return {
    "--forms-input-px": "11px",
    "--forms-input-py": "8px",
    "--forms-input-font": "13px",
    "--forms-label-font": "13px",
    "--forms-tab-font": "12px",
  };
}

/**
 * CSS custom properties for a resolved theme — the web provider's `style`.
 * Emits the per-form accent (+ derived tints/hover/focus-ring) AND the static
 * tokens, so anything wrapped in a FormThemeProvider gets the full set.
 */
export function formThemeCssVars(
  theme: FormTheme,
): Record<string, string> {
  return {
    ...formStaticCssVars(),
    "--forms-accent": theme.accent,
    "--forms-accent-contrast": theme.accentContrast,
    /** ~6% accent — soft backgrounds (drop zones, selected tint). */
    "--forms-accent-soft": rgba(theme.accent, 0.06),
    /** 50% accent — hover borders. */
    "--forms-accent-50": rgba(theme.accent, 0.5),
    /** Darkened accent — solid button hover. */
    "--forms-accent-hover": darken(theme.accent, 0.12),
    /** 12% accent — input focus ring. */
    "--forms-accent-ring": rgba(theme.accent, 0.14),
    "--forms-btn-radius": `${theme.button.radius}px`,
  };
}
